import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import uploadAlertImage from '../../../images/23_upload_alert.png';

export type ImageAddCandidate =
  | { type: 'file'; file: File }
  | { type: 'url'; url: string };

export interface ImageAddDialogProps {
  open: boolean;
  title?: string;
  onClose: () => void;
  onAdd: (candidates: ImageAddCandidate[]) => void;
  disableAutoFocus?: boolean;
  disableRestoreFocus?: boolean;
}

type Mode = 'upload' | 'paste' | 'url';

const normalizeUrl = (raw: string) => String(raw ?? '').trim();

type FilePreview = { file: File; previewUrl: string; name: string };

export const isValidImageUrl = (raw: string) => {
  const s = normalizeUrl(raw);
  if (!s) return false;
  if (s.startsWith('data:image/')) return true;
  try {
    const u = new URL(s);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
};

const ImageAddDialog: React.FC<ImageAddDialogProps> = ({
  open,
  title = '画像を追加',
  onClose,
  onAdd,
  disableAutoFocus = false,
  disableRestoreFocus = false,
}) => {
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';

  const enableUrlMode = false;
  const [mode, setMode] = useState<Mode>('upload');
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [pastedFiles, setPastedFiles] = useState<File[]>([]);
  const [urlValue, setUrlValue] = useState<string>('');

  const [uploadPreviews, setUploadPreviews] = useState<FilePreview[]>([]);
  const [pastePreviews, setPastePreviews] = useState<FilePreview[]>([]);

  const pasteAreaRef = useRef<HTMLDivElement | null>(null);

  const reset = useCallback(() => {
    setMode('upload');
    setUploadFiles([]);
    setPastedFiles([]);
    setUrlValue('');
  }, []);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  useEffect(() => {
    if (!enableUrlMode && mode === 'url') setMode('upload');
  }, [enableUrlMode, mode]);

  useEffect(() => {
    const previews = uploadFiles.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      name: file.name || 'image',
    }));
    setUploadPreviews(previews);
    return () => previews.forEach((p) => URL.revokeObjectURL(p.previewUrl));
  }, [uploadFiles]);

  useEffect(() => {
    const previews = pastedFiles.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      name: file.name || 'image',
    }));
    setPastePreviews(previews);
    return () => previews.forEach((p) => URL.revokeObjectURL(p.previewUrl));
  }, [pastedFiles]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleFilesPicked = useCallback((files: FileList | null) => {
    if (!files) return;
    const picked = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (picked.length > 0) {
      setUploadFiles((prev) => [...prev, ...picked]);
    }
  }, []);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items || items.length === 0) return;

    const images: File[] = [];
    for (const item of Array.from(items)) {
      if (item.kind !== 'file') continue;
      const f = item.getAsFile();
      if (!f) continue;
      if (!f.type.startsWith('image/')) continue;
      images.push(f);
    }

    if (images.length > 0) {
      e.preventDefault();
      // Screenshots often share a file name; keep each paste as a separate image.
      setPastedFiles((prev) => [...prev, ...images]);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (mode !== 'paste') return;
    const t = window.setTimeout(() => pasteAreaRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open, mode]);

  const urlOk = useMemo(() => isValidImageUrl(urlValue), [urlValue]);

  const candidates = useMemo<ImageAddCandidate[]>(() => {
    if (mode === 'upload') return uploadFiles.map((file) => ({ type: 'file', file }));
    if (mode === 'paste') return pastedFiles.map((file) => ({ type: 'file', file }));
    const u = normalizeUrl(urlValue);
    return urlOk && u ? [{ type: 'url', url: u }] : [];
  }, [mode, uploadFiles, pastedFiles, urlValue, urlOk]);

  const canAdd = candidates.length > 0;

  const handleAdd = useCallback(() => {
    if (!canAdd) return;
    onAdd(candidates);
    onClose();
  }, [canAdd, candidates, onAdd, onClose]);

  const PreviewGrid: React.FC<{ items: FilePreview[] }> = ({ items }) => {
    if (items.length === 0) {
      return (
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          まだ画像が選択されていません
        </Typography>
      );
    }
    return (
      <Box
        sx={{
          mt: 0.75,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
        }}
      >
        {items.map((p, idx) => (
          <Box
            key={`${p.name}-${idx}`}
            sx={{
              width: 104,
              height: 78,
              position: 'relative',
              borderRadius: 1,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: isDark ? '#1e293b' : '#fff',
            }}
          >
            <Box
              component="img"
              src={p.previewUrl}
              alt={p.name}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        disableAutoFocus={disableAutoFocus}
        disableRestoreFocus={disableRestoreFocus}
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
          <Tabs
            value={mode}
            onChange={(_e, v) => setMode(v)}
            aria-label="image add mode tabs"
            sx={{
              mb: 1,
              ...(isDark && {
                '& .MuiTab-root': {
                  color: '#94a3b8',
                },
                '& .MuiTab-root.Mui-selected': {
                  color: '#38bdf8',
                  fontWeight: 700,
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#38bdf8',
                },
              }),
            }}
          >
            <Tab value="upload" label="アップロード" />
            <Tab value="paste" label="貼り付け" />
            {enableUrlMode && <Tab value="url" label="URL" />}
          </Tabs>

        {mode === 'upload' && (
          <Box>
            <Button
              variant="outlined"
              component="label"
              sx={isDark ? {
                color: '#fff',
                backgroundColor: '#0284c7',
                borderColor: '#0284c7',
                '&:hover': {
                  backgroundColor: '#0ea5e9',
                  borderColor: '#0ea5e9',
                },
              } : {}}
            >
              ファイル選択
              <input
                hidden
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  handleFilesPicked(e.target.files);
                  e.target.value = '';
                }}
              />
            </Button>
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              1枚または複数枚を選択できます。続けて選択すると画像が追加されます。
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  選択中: {uploadPreviews.length}件
                </Typography>
                {uploadPreviews.length > 0 && (
                  <IconButton
                    size="small"
                    aria-label="clear upload selections"
                    onClick={() => setUploadFiles([])}
                    sx={{ p: 0.25, m: 0, alignSelf: 'center' }}
                    style={{ marginTop: 0 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <PreviewGrid items={uploadPreviews} />
            </Box>
          </Box>
        )}

        {mode === 'paste' && (
          <Box>
            <Paper
              ref={pasteAreaRef}
              role="group"
              aria-label="スクリーンショット貼り付け"
              tabIndex={0}
              onPaste={handlePaste}
              variant="outlined"
              sx={{
                p: 1,
                minHeight: 96,
                outline: 'none',
                borderStyle: 'dashed',
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                ここをクリックしてから、スクリーンショットを貼り付けてください (Ctrl+V)
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                続けて貼り付けると画像が追加されます。すべて貼り付けてから「追加」を押してください。
              </Typography>
              <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2">取得: {pastePreviews.length}件</Typography>
                {pastePreviews.length > 0 && (
                  <IconButton
                    size="small"
                    aria-label="clear paste selections"
                    onClick={() => setPastedFiles([])}
                    sx={{ p: 0.25, m: 0, alignSelf: 'center' }}
                    style={{ marginTop: 0 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <PreviewGrid items={pastePreviews} />
            </Paper>
          </Box>
        )}

        {enableUrlMode && mode === 'url' && (
          <Box>
            <TextField
              fullWidth
              label="画像URL"
              value={urlValue}
              onChange={(e) => setUrlValue(e.target.value)}
              placeholder="https://..."
              error={!!urlValue && !urlOk}
              helperText={
                !urlValue
                  ? 'http(s) URL または data:image/... を入力してください'
                  : urlOk
                  ? 'OK'
                  : 'URL形式が正しくありません'
              }
            />
            {urlOk && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  プレビュー
                </Typography>
                <Box
                  component="img"
                  src={normalizeUrl(urlValue)}
                  alt="preview"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: 180,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    objectFit: 'contain',
                  }}
                />
              </Box>
            )}
          </Box>
        )}
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: isDark ? '#1e293b' : '#f5f5f5',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              flex: { xs: '0 0 auto', sm: '1 1 50%' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component="img"
              src={uploadAlertImage}
              alt="upload alert"
              sx={{
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: '#888686',
              }}
            />
          </Box>
          <Box sx={{ flex: { xs: '0 0 auto', sm: '1 1 50%' } }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#f00' }}>
              機密データの流出警告について
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              週報アプリは &quot;Confidential C&quot; までのアップロードが許可されています。
              内容を確認しAllowを押下してください。
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.75 }}>
              ※情報セキュリティ委員会に本警告の除外を申請中です。（2026/2/4 現在）
            </Typography>
          </Box>
        </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end', alignItems: 'center' }}>
          <Button
            onClick={handleClose}
            sx={isDark ? {
              color: '#cbd5e1',
              '&:hover': { backgroundColor: 'rgba(148,163,184,0.08)' },
            } : {}}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={!canAdd}
            sx={isDark ? {
              color: '#fff',
              backgroundColor: '#0284c7',
              '&:hover': { backgroundColor: '#0ea5e9' },
              '&.Mui-disabled': { backgroundColor: '#334155', color: '#94a3b8' },
            } : { color: '#fff' }}
          >
            追加
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ImageAddDialog;
